"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { createTranslator } from "@/lib/i18n/nested";
import en from "@/lib/i18n/messages/en.json";
import ru from "@/lib/i18n/messages/ru.json";

export type Locale = "ru" | "en";

const STORAGE_KEY = "keiront.locale";

const MESSAGES: Record<Locale, typeof ru> = { ru, en };

type I18nContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
  messages: typeof ru;
};

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({
  children,
  initialLocale,
}: {
  children: ReactNode;
  /**
   * Локаль, вычисленная на сервере (root layout читает заголовок `x-locale`,
   * который выставляет middleware для путей `/en` `/ru`). Делает SSR и первый
   * клиентский рендер консистентными — без мигания языка.
   */
  initialLocale?: Locale;
}) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale ?? "ru");

  useEffect(() => {
    // На локализованных публичных путях источник истины — URL/`initialLocale`
    // (он же синхронизирован с cookie middleware'ом). В кабинете (без префикса
    // локали) применяем сохранённый в localStorage выбор пользователя.
    if (initialLocale) return;
    try {
      const s = localStorage.getItem(STORAGE_KEY);
      if (s === "en" || s === "ru") {
        setLocaleState(s);
        document.cookie = `${STORAGE_KEY}=${s};path=/;max-age=31536000`;
      }
    } catch {
      /* ignore */
    }
  }, [initialLocale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
      document.cookie = `${STORAGE_KEY}=${next};path=/;max-age=31536000`;
    } catch {
      /* ignore */
    }
  }, []);

  const messages = MESSAGES[locale];

  const t = useMemo(() => createTranslator(messages), [messages]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale === "en" ? "en" : "ru";
    }
  }, [locale]);

  const value = useMemo(
    () => ({ locale, setLocale, t, messages }),
    [locale, setLocale, t, messages],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used within I18nProvider");
  }
  return ctx;
}
